import { ApiService } from '../../../services/ApiService.js';

/**
 * @typedef {import('../../../types/api.d.js').ApiAiAnnouncementData} ApiAiAnnouncementData
 */

const CACHE_TIME = 60 * 1000; // 1 minute

const api = new ApiService();

class AiAnnouncementStoreClass {
    /** @type {ApiAiAnnouncementData[]} */
    #state = $state([]);
    /** @type {boolean} */
    isLoading = $state(false);
    /** @type {number} */
    #loadedAt = 0;
    /** @type {Promise<void>|null} */
    #loadingPromise = null;

    constructor() {
        void this.reset();
    }

    /**
     * @returns {ApiAiAnnouncementData[]}
     */
    get list() {
        return this.#state;
    }

    /**
     * @returns {{
     *     userId:number,
     *     count:number,
     *     highestRisk:string,
     *     hasUnknown:boolean,
     * }[]}
     */
    getUserStatistics() {
        /** @type {Map<number, {userId:number,count:number,highestRisk:string,hasUnknown:boolean}>} */
        const result = new Map();

        for (const item of this.#state) {
            const userId = item.user_id ?? 0;

            if (userId === 0) {
                continue;
            }

            if (!result.has(userId)) {
                result.set(userId, {
                    userId,
                    count: 0,
                    highestRisk: '',
                    hasUnknown: false,
                });
            }

            const stat = result.get(userId);

            stat.count++;

            if (item.risk === '') {
                stat.hasUnknown = true;
            } else if (item.risk === 'prohibited') {
                stat.highestRisk = 'prohibited';
            } else if (item.risk === 'high-risk' && stat.highestRisk !== 'prohibited') {
                stat.highestRisk = 'high-risk';
            }
        }

        return Array.from(result.values());
    }

    /**
     * @returns {Promise<void>}
     */
    async reset() {
        if (this.#loadingPromise) {
            return this.#loadingPromise;
        }

        this.isLoading = true;

        this.#loadingPromise = (async () => {
            try {
                console.log('Loading AI announcements');
                const response = await api.getAiAnnouncement();

                if (response.ok) {
                    this.#state = response.data.items;
                    this.#loadedAt = Date.now();
                } else {
                    this.#state = [];
                    this.#loadedAt = 0;
                }
            } finally {
                this.isLoading = false;
                this.#loadingPromise = null;
            }
        })();

        return this.#loadingPromise;
    }

    /**
     * @returns {Promise<void>}
     */
    async #ensureLoaded() {
        if (this.#state.length === 0 || Date.now() - this.#loadedAt > CACHE_TIME) {
            await this.reset();
        }
    }

    /**
     * Sucht einen Benutzer anhand einer Bedingung.
     * Wird kein Benutzer gefunden, wird der Cache einmal aktualisiert
     * und die Suche wiederholt.
     *
     * @param {(entry: ApiAiAnnouncementData) => boolean} predicate
     * @returns {Promise<ApiAiAnnouncementData|null>}
     */
    async #findEntry(predicate) {
        await this.#ensureLoaded();

        let user = this.#state.find(predicate);

        if (user) {
            return user;
        }

        await this.reset();

        return this.#state.find(predicate) ?? null;
    }

    /**
     * @param {number} userId
     * @returns {Promise<ApiAiAnnouncementData|null>}
     */
    async findEntryByUserId(userId) {
        return this.#findEntry((entry) => entry.user_id === userId);
    }
}

export const AiAnnouncementStore = new AiAnnouncementStoreClass();
